import { type CSSProperties, forwardRef, memo } from 'react'
import { Input } from 'antd'
import type { TextAreaProps, TextAreaRef } from 'antd/es/input/TextArea'
import { useSnapshot } from 'valtio'

import { chatState, chatAction, configState } from '@/store'
import { useStyles } from './style'
import FooterAddition from './FooterAddition'
import useChineseInput from '@/hooks/useChineseInput'
import DragUpload from '@/components/DragUpload'
import useDragUpload from '@/hooks/useDragUpload'
import FileItem from '@/components/FileItems'

export interface ChatInputAreaProps extends Omit<TextAreaProps, 'classNames'> {
  onSend: () => void
  onStop: () => void
  loading?: boolean
  className?: string
  style?: CSSProperties
}

const ChatInputArea = forwardRef<TextAreaRef, ChatInputAreaProps>(
  ({ onSend, onStop, loading, className, style }, ref) => {
    const { inputText, inputFileList } = useSnapshot(chatState)
    const { baseConfig } = useSnapshot(configState)
    const { isDragging, handlePaste } = useDragUpload()

    const { cx, styles } = useStyles()
    const {
      curValue,
      handleChange,
      handleCompositionStart,
      handleCompositionEnd,
    } = useChineseInput(inputText, (value: string) => {
      chatAction.setInputText(value)
    })

    return (
      <section className={cx(styles.chatInputArea, className)} style={style}>
        <DragUpload isDragging={isDragging} />
        {inputFileList.length > 0 && (
          <div className="flex flex-wrap gap-3 px-3">
            {inputFileList.map(file => (
              <FileItem
                key={file.uid}
                file={file}
                onClose={() => {
                  chatState.inputFileList = chatState.inputFileList.filter(
                    i => i.uid !== file.uid,
                  )
                }}
              />
            ))}
          </div>
        )}
        <div className={styles.textAreaWrap}>
          <Input.TextArea
            id="chatInput"
            autoFocus
            ref={ref}
            value={curValue}
            maxLength={baseConfig.limitLength}
            placeholder="请输入您的问题..."
            autoSize={{ maxRows: 3, minRows: 1 }}
            onChange={handleChange}
            onPaste={handlePaste}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onPressEnter={(e: any) => {
              if (e.key === 'Enter' && e.shiftKey) return
              if (e.keyCode !== 13 || e.isComposing) return
              e.preventDefault()
              onSend()
            }}
          />
        </div>
        <FooterAddition
          curValue={curValue}
          loading={loading}
          onSend={onSend}
          onStop={onStop}
        />
      </section>
    )
  },
)

export default memo(ChatInputArea)
